import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Popconfirm } from 'antd';
import { setGenerationArchived } from './api';
import { GenerationThumb, ImageGallery } from './GenerationGallery';
import { libraryItems, libraryLabel } from './animationLibrary';
import './AnimationStudio.css';

const PAGE_SIZE = 24;

function isArchived(item) {
  return item?.status === 'archived' || Boolean(item?.archivedAt) || item?.archived;
}

function LibraryGrid({
  projectId,
  items,
  people,
  archived,
  archivingId,
  deletingId,
  onArchive,
  onDelete,
}) {
  const [count, setCount] = useState(PAGE_SIZE);
  const marker = useRef(null);
  const visible = items.slice(0, count);
  const hasMore = count < items.length;

  useEffect(() => {
    setCount(PAGE_SIZE);
  }, [items]);

  useEffect(() => {
    const node = marker.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCount((value) => Math.min(items.length, value + PAGE_SIZE));
        }
      },
      { rootMargin: '480px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, items.length]);

  if (!items.length) {
    return <p>{archived ? 'No archived pictures.' : 'No pictures yet.'}</p>;
  }

  return (
    <ImageGallery>
      <ul className="era-scene-gallery">
        {visible.map((item) => {
          const label = libraryLabel(item, people);
          return (
            <li key={item.id} className="era-scene-gallery__item">
              <GenerationThumb
                projectId={projectId}
                generation={item}
                alt={label}
                caption={label}
                className="generation-thumb--gallery"
              />
              <div className="era-scene-gallery__actions">
                <Button
                  loading={archivingId === item.id}
                  onClick={() => onArchive(item, !archived)}
                >
                  {archived ? 'Restore' : 'Archive'}
                </Button>
                <Popconfirm
                  title="Delete this picture?"
                  okText="Yes, Delete"
                  cancelText="No, cancel"
                  okButtonProps={{ danger: true, size: 'small' }}
                  cancelButtonProps={{ size: 'small' }}
                  onConfirm={() => onDelete?.(item)}
                >
                  <Button danger loading={deletingId === item.id}>
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            </li>
          );
        })}
      </ul>
      {hasMore ? <div ref={marker} className="library-more" aria-hidden="true" /> : null}
    </ImageGallery>
  );
}

export default function ClipLibrary({
  projectId,
  characters,
  generations,
  onGenerated,
  onDeleteGeneration,
  deletingId,
}) {
  const people = useMemo(
    () => (characters || []).filter((character) => character?.id && !isArchived(character)),
    [characters]
  );
  const active = useMemo(() => libraryItems(generations), [generations]);
  const archived = useMemo(() => libraryItems(generations, { archived: true }), [generations]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivingId, setArchivingId] = useState('');
  const [notice, setNotice] = useState(null);

  async function archiveItem(generation, nextArchived) {
    setArchivingId(generation.id);
    setNotice(null);
    try {
      const data = await setGenerationArchived(projectId, generation.id, nextArchived);
      if (data?.generation) onGenerated?.(data.generation);
      setNotice({
        type: 'success',
        text: nextArchived ? 'Archived. Open Archived to bring it back.' : 'Restored to the library.',
      });
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Could not update this picture.' });
    } finally {
      setArchivingId('');
    }
  }

  return (
    <div className="animation-studio">
      {notice ? <Alert type={notice.type} showIcon title={notice.text} /> : null}
      <section className="animation-panel" aria-labelledby="animation-library">
        <div>
          <h2 id="animation-library">Library</h2>
          <p>Pictures and clips from this book, newest first. Click a thumbnail to open it.</p>
        </div>
        <LibraryGrid
          projectId={projectId}
          items={active}
          people={people}
          archivingId={archivingId}
          deletingId={deletingId}
          onArchive={archiveItem}
          onDelete={onDeleteGeneration}
        />
        {archived.length > 0 ? (
          <div className="library-archived">
            <button
              type="button"
              className="library-archived-toggle"
              aria-expanded={archivedOpen}
              onClick={() => setArchivedOpen((open) => !open)}
            >
              {archivedOpen ? 'Hide archived' : `Archived (${archived.length})`}
            </button>
            {archivedOpen ? (
              <LibraryGrid
                projectId={projectId}
                items={archived}
                people={people}
                archived
                archivingId={archivingId}
                deletingId={deletingId}
                onArchive={archiveItem}
                onDelete={onDeleteGeneration}
              />
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
